import { css } from "@emotion/react";
import { format } from "date-fns";
import { FC } from "react";
import { useSelector } from "react-redux";

import { ChevronLeft, ChevronRight } from "@livechat/design-system-icons";
import { Button, DesignToken, Loader, SpacingToken, Text } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { SettingsTable, SettingsTableColumn, SettingsTableRow } from "components/settings-table";
import { DateFormat } from "constants/date";
import { ReportType } from "constants/reports/report-type";
import { useLastVisitedReport } from "hooks/reports/use-last-visited-report";
import { useAuditLog } from "hooks/use-audit-log";
import { selectAuditLogTargets } from "store/entities/application-settings/selectors";
import { selectHasAuditLog } from "store/entities/license";
import { withVar } from "themes/helpers";

import { Filters } from "../components/Filters/Filters";
import { UpgradeReportPage } from "../components/UpgradeReportPage";

import { AuthorCell } from "./AuthorCell";
import { EmptyState } from "./EmptyState";
import { IconNameMapper } from "./IconNameMapper";

type TableHeaderProps = { column: number; columns: number };
type ColumnProps = { children: React.ReactNode };

const TableHeader: React.FC<TableHeaderProps> = ({ column, columns }: TableHeaderProps) => {
  const index = (column - 1) % columns;
  if (index === 0) {
    return <>Change</>;
  }

  if (index === 1) {
    return <>Made by</>;
  }

  if (index === 2) {
    return <>Date</>;
  }

  return null;
};

const Column: React.FC<ColumnProps> = ({ children }: ColumnProps) => (
  <SettingsTableColumn paddingDesktop={`${withVar(SpacingToken.Spacing4)}`}>{children}</SettingsTableColumn>
);

export const AuditLog: FC = () => {
  useLastVisitedReport(ReportType.AuditLog);
  const hasAuditLog = useSelector(selectHasAuditLog);
  if (!hasAuditLog)
    return (
      <UpgradeReportPage
        heading="Boost accountability with audit logs"
        description="Track every change made and provide full transparency of your operations. Browse your team's complex
          actions without losing control."
      />
    );
  const auditLogTargets = useSelector(selectAuditLogTargets);
  const { data, isFetching, fetchNextPage, fetchPreviousPage, hasNextPage, hasPreviousPage } = useAuditLog();

  return (
    <div>
      <Filters disableDistribution />
      {(!data || !data.length) && !isFetching ? (
        <EmptyState />
      ) : (
        <>
          <Flex
            gap="8px"
            alignItems="center"
            justifyContent="flex-end"
            paddingRight={withVar(SpacingToken.Spacing5)}
            borderBottom={`1px solid ${withVar(DesignToken.BorderBasicSecondary)}`}
          >
            <Button
              icon={<ChevronLeft />}
              kind="plain"
              size="compact"
              onClick={() => {
                fetchPreviousPage();
              }}
              disabled={!hasPreviousPage || isFetching}
            />
            <Button
              icon={<ChevronRight />}
              kind="plain"
              size="compact"
              onClick={() => {
                fetchNextPage();
              }}
              disabled={!hasNextPage || isFetching}
            />
          </Flex>
          {isFetching ? (
            <Loader
              css={css({
                width: "100%",
                height: "calc(100vh - 156px)",
                alignContent: "center",
                justifyContent: "center",
              })}
            />
          ) : (
            <SettingsTable
              columns={3}
              marginTop={withVar(SpacingToken.Spacing5)}
              marginBottom="0px"
              columnSizes="1fr 1fr 1fr"
              headers={TableHeader}
            >
              {data.map((item, index) => (
                <SettingsTableRow key={item.ID} data-testid={`log-row-${index + 1}`}>
                  <Column>
                    <Flex column>
                      <Flex column={false}>
                        <IconNameMapper iconName={auditLogTargets[item.target]?.iconName} size="small" />
                        <Block marginLeft={withVar(SpacingToken.Spacing2)} />
                        <Text
                          data-testid={`${item.target}-${item.action}`}
                        >{`${auditLogTargets[item.target]?.labelSingular} ${item.action}`}</Text>
                      </Flex>
                      <Text
                        data-testid={`entity-name-${item.entityName}`}
                        size="sm"
                        css={css({ marginLeft: withVar(SpacingToken.Spacing6) })}
                        customColor={withVar(DesignToken.ContentBasicSecondary)}
                      >
                        {item.entityName}
                      </Text>
                    </Flex>
                  </Column>
                  <Column>
                    <AuthorCell
                      ID={item.author.ID}
                      type={item.author.type}
                      ip={item.author.ip}
                      userAgent={item.author.userAgent}
                      accountID={item.author?.accountID}
                    />
                  </Column>
                  <Column>
                    <Flex>
                      <Text>{format(new Date(item.createdAt), DateFormat.MonthDayYearTime)}</Text>
                    </Flex>
                  </Column>
                </SettingsTableRow>
              ))}
            </SettingsTable>
          )}
        </>
      )}
    </div>
  );
};
